import React, { useState, useEffect, useRef } from 'react';
import { Layout, Card, Typography, Spin, Empty, Badge, Tag, Space, Input, Pagination, Select, Button, DatePicker, Modal, App } from 'antd';
import { EnvironmentOutlined, ToolOutlined, SearchOutlined, CalendarOutlined, LeftOutlined, RightOutlined, PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { 
  Wrench, 
  FlashOff, 
  Wind, 
  Building, 
  Packages 
} from 'iconoir-react';
// @ts-ignore
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { maximoApi, WorkOrder, Team, PaginatedWorkOrderResponse } from '../../lib/maximoApi';
import WorkOrderCalendar from '../../components/WorkOrderCalendar';
import CreateWorkOrderForm from '../../components/CreateWorkOrderForm';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

// Helper function to get team icon and clean name
const getTeamInfo = (team: Team) => {
  const cleanName = team.name.replace(/-TEAM$/, '').replace(/-/g, ' ');
  
  let icon;
  if (team.id.includes('MECH')) {
    icon = <Wrench width={16} height={16} className="text-blue-600" />;
  } else if (team.id.includes('ELEC')) {
    icon = <FlashOff width={16} height={16} className="text-yellow-600" />;
  } else if (team.id.includes('HVAC')) {
    icon = <Wind width={16} height={16} className="text-green-600" />;
  } else if (team.id.includes('FAC')) {
    icon = <Building width={16} height={16} className="text-purple-600" />;
  } else if (team.id.includes('PLUMB')) {
    icon = <Packages width={16} height={16} className="text-indigo-600" />;
  } else {
    icon = <Wrench width={16} height={16} className="text-gray-600" />;
  }
  
  return { cleanName, icon };
};

interface WorkOrderCardProps {
  workOrder: WorkOrder;
  isDragDisabled?: boolean;
  sourceTeam?: string;
}

const WorkOrderCard: React.FC<WorkOrderCardProps> = ({ workOrder, isDragDisabled = false, sourceTeam = 'unassigned' }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'workOrder',
    item: () => {
      console.log(`Dragging work order ${workOrder.id} from ${sourceTeam}`);
      return {
        id: workOrder.id, 
        workOrder, 
        sourceTeam
      };
    },
    canDrag: !isDragDisabled,
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const getPriorityColor = (priority: number) => {
    if (priority <= 1) return 'red';
    if (priority <= 3) return 'orange';
    return 'blue';
  };

  return (
    <div
      ref={drag}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragDisabled ? 'not-allowed' : 'move',
      }}
    >
      <Card
        size="small"
        className={`mb-2 transition-all ${!isDragDisabled ? 'hover:shadow-md' : 'opacity-60'}`}
        styles={{ body: { padding: '8px 12px' } }}
      >
        <div className="flex justify-between items-start mb-1">
          <Text strong className="text-sm">{workOrder.wonum}</Text>
          <Tag color={getPriorityColor(workOrder.priority)}>P{workOrder.priority}</Tag>
        </div>
        <Text className="text-xs block mb-2" ellipsis={{ tooltip: true }}>
          {workOrder.description}
        </Text>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          {workOrder.location && (
            <span className="flex items-center gap-1">
              <EnvironmentOutlined />
              {workOrder.location}
            </span>
          )}
          <span className="flex items-center gap-1">
            <ToolOutlined />
            {workOrder.workType}
          </span>
        </div>
      </Card>
    </div>
  );
};

interface TeamColumnProps {
  team: Team;
  workOrders: WorkOrder[];
  onDrop: (workOrderId: string, teamId: string, sourceTeam?: string) => void;
  loading: boolean;
  assigningWorkOrders: Set<string>;
}

const TeamColumn: React.FC<TeamColumnProps> = ({ team, workOrders, onDrop, loading, assigningWorkOrders }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'workOrder',
    canDrop: (item: { id: string; sourceTeam?: string }) => {
      const canDropResult = item.sourceTeam !== team.id && !assigningWorkOrders.has(item.id);
      console.log(`Can drop ${item.id} on team ${team.id}? ${canDropResult} (sourceTeam: ${item.sourceTeam}, isAssigning: ${assigningWorkOrders.has(item.id)})`);
      return canDropResult;
    },
    drop: (item: { id: string; sourceTeam?: string }) => {
      console.log(`Dropping ${item.id} from ${item.sourceTeam} to team ${team.id}`);
      onDrop(item.id, team.id, item.sourceTeam);
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const { cleanName, icon } = getTeamInfo(team);
  const isAssigning = workOrders.some(wo => assigningWorkOrders.has(wo.id));

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-medium">{cleanName}</span>
          </div>
          <div className="flex items-center gap-2">
            {isAssigning && <LoadingOutlined spin />}
            <Badge count={workOrders.length} showZero style={{ backgroundColor: '#ec4899' }} />
          </div>
        </div>
      }
      className="h-full shadow-sm"
      styles={{
        body: {
          height: 'calc(100% - 57px)',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          backgroundColor: isOver && canDrop ? '#fef3f3' : isOver && !canDrop ? '#fff5f5' : 'white',
          transition: 'background-color 0.2s',
          padding: '12px',
        }
      }}
    >
      <div ref={drop} className="min-h-full">
        {loading ? (
          <div className="text-center py-8">
            <Spin />
          </div>
        ) : workOrders.length === 0 ? (
          <Empty
            description="No work orders assigned"
            className="py-8"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          workOrders.map((wo) => (
            <WorkOrderCard 
              key={wo.id} 
              workOrder={wo} 
              isDragDisabled={assigningWorkOrders.has(wo.id)}
              sourceTeam={team.id}
            />
          ))
        )}
      </div>
    </Card>
  );
};

interface UnassignedColumnProps {
  workOrders: WorkOrder[];
  onDrop: (workOrderId: string) => void;
  loading: boolean;
  assigningWorkOrders: Set<string>;
  paginationData: PaginatedWorkOrderResponse | null;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const UnassignedColumn: React.FC<UnassignedColumnProps> = ({ 
  workOrders, 
  onDrop, 
  loading, 
  assigningWorkOrders,
  paginationData,
  currentPage,
  onPageChange
}) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'workOrder',
    canDrop: (item: { id: string; sourceTeam?: string }) => {
      // Can only drop if it's from a team (not already unassigned)
      const canDropResult = item.sourceTeam !== 'unassigned' && !assigningWorkOrders.has(item.id);
      console.log(`Can drop ${item.id} on unassigned? ${canDropResult} (sourceTeam: ${item.sourceTeam}, isAssigning: ${assigningWorkOrders.has(item.id)})`);
      return canDropResult;
    },
    drop: (item: { id: string; sourceTeam?: string }) => {
      console.log(`Dropping ${item.id} from ${item.sourceTeam} to unassigned`);
      if (item.sourceTeam !== 'unassigned') {
        onDrop(item.id);
      }
    },
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  return (
    <div 
      ref={drop}
      className={`h-full ${isOver && canDrop ? 'bg-pink-50' : isOver && !canDrop ? 'bg-red-50' : ''} transition-colors`}
    >
      {loading ? (
        <div className="text-center py-8">
          <Spin />
        </div>
      ) : workOrders.length === 0 ? (
        <Empty description="No unassigned work orders" />
      ) : (
        <>
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
            {workOrders.map((workOrder) => (
              <WorkOrderCard
                key={workOrder.id}
                workOrder={workOrder}
                isDragDisabled={assigningWorkOrders.has(workOrder.id)}
                sourceTeam="unassigned"
              />
            ))}
          </div>
          
          {/* Pagination */}
          {paginationData && paginationData.totalPages > 1 && (
            <div className="mt-4 text-center">
              <Pagination
                size="small"
                current={currentPage}
                total={paginationData.total}
                pageSize={paginationData.limit}
                onChange={onPageChange}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DemoMaximoWorkOrders: React.FC = () => {
  const { message } = App.useApp();
  const [unassignedWorkOrders, setUnassignedWorkOrders] = useState<WorkOrder[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamWorkOrders, setTeamWorkOrders] = useState<Record<string, WorkOrder[]>>({});
  const [loading, setLoading] = useState(true);
  const [assigningWorkOrders, setAssigningWorkOrders] = useState<Set<string>>(new Set());
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [createWorkOrderVisible, setCreateWorkOrderVisible] = useState(false);
  
  // Pagination and search state
  const [paginationData, setPaginationData] = useState<PaginatedWorkOrderResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<number | undefined>(undefined);
  const [workTypeFilter, setWorkTypeFilter] = useState<string | undefined>(undefined);
  const [dateFilter, setDateFilter] = useState<string | undefined>(dayjs().format('YYYY-MM-DD'));
  
  // Horizontal scroll for teams
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log('Demo Maximo: Initial load with today date filter');
    loadInitialData();
  }, []);

  const loadInitialData = async (page: number = 1, search?: string, priority?: number, workType?: string) => {
    try {
      setLoading(true);
      const [unassignedRes, teamsRes] = await Promise.all([
        maximoApi.getUnassignedWorkOrders({
          page,
          limit: 20,
          search,
          priority,
          workType
        }),
        maximoApi.getTeams(),
      ]);

      setPaginationData(unassignedRes.data);
      setUnassignedWorkOrders(unassignedRes.data.data);
      setTeams(teamsRes.data);

      await loadTeamWorkOrders(teamsRes.data, dateFilter);
    } catch (error) {
      console.error('Demo Maximo: Error loading initial data:', error);
      message.error('Failed to load Maximo demo data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamWorkOrders = async (teamsList: Team[], dateFilterParam?: string) => {
    const teamOrders: Record<string, WorkOrder[]> = {};
    const filterDate = dateFilterParam !== undefined ? dateFilterParam : dateFilter;
    
    console.log(`Demo Maximo: Loading team work orders with date filter: ${filterDate || 'backend default (today)'}`);
    
    await Promise.all(
      teamsList.map(async (team) => {
        try {
          const params: any = { limit: 10 };
          if (filterDate) {
            params.date = filterDate;
          }
          
          const res = await maximoApi.getTeamWorkOrders(team.id, params);
          teamOrders[team.id] = res.data;
          console.log(`Demo Maximo: Team ${team.name}: ${res.data.length} work orders loaded`);
        } catch (error) {
          console.error(`Demo Maximo: Error loading work orders for team ${team.id}:`, error);
          teamOrders[team.id] = [];
        }
      })
    );
    setTeamWorkOrders(teamOrders);
  };

  const handleWorkOrderDrop = async (workOrderId: string, teamId: string, sourceTeam?: string) => {
    if (assigningWorkOrders.has(workOrderId)) {
      return;
    }

    try {
      setAssigningWorkOrders(prev => new Set(prev).add(workOrderId));
      
      console.log(`Demo Maximo: Assigning work order ${workOrderId} from ${sourceTeam} to team ${teamId}`);
      
      await maximoApi.assignWorkOrder({ workOrderId, teamId });
      
      // Update local state optimistically
      if (sourceTeam === 'unassigned') {
        setUnassignedWorkOrders(prev => prev.filter(wo => wo.id !== workOrderId));
        if (paginationData) {
          setPaginationData(prev => prev ? {
            ...prev,
            total: prev.total - 1,
            data: prev.data.filter(wo => wo.id !== workOrderId)
          } : prev);
        }
      } else if (sourceTeam) {
        setTeamWorkOrders(prev => ({
          ...prev,
          [sourceTeam]: prev[sourceTeam]?.filter(wo => wo.id !== workOrderId) || []
        }));
      }
      
      const workOrderToMove = sourceTeam === 'unassigned' 
        ? unassignedWorkOrders.find(wo => wo.id === workOrderId)
        : sourceTeam ? teamWorkOrders[sourceTeam]?.find((wo: WorkOrder) => wo.id === workOrderId) : null;
        
      if (workOrderToMove) {
        const updatedWorkOrder = { 
          ...workOrderToMove, 
          assignedTeam: teamId, 
          status: 'INPRG' as const 
        };
        
        setTeamWorkOrders(prev => ({
          ...prev,
          [teamId]: [
            updatedWorkOrder, 
            ...(prev[teamId]?.filter(wo => wo.id !== workOrderId) || [])
          ]
        }));
      }
      
      const targetTeam = teams.find(t => t.id === teamId);
      if (targetTeam) {
        message.success(`Demo: Work order assigned to ${getTeamInfo(targetTeam).cleanName}`);
      } else {
        message.success('Demo: Work order assigned successfully');
      }
    } catch (error) {
      console.error('Demo Maximo: Error assigning work order:', error);
      message.error('Failed to assign work order in demo');
      loadInitialData(currentPage, searchText, priorityFilter, workTypeFilter);
      if (teams.length > 0) {
        loadTeamWorkOrders(teams);
      }
    } finally {
      setAssigningWorkOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(workOrderId);
        return newSet;
      });
    }
  };

  const handleUnassignWorkOrder = async (workOrderId: string) => {
    if (assigningWorkOrders.has(workOrderId)) {
      return;
    }

    try {
      setAssigningWorkOrders(prev => new Set(prev).add(workOrderId));
      
      console.log(`Demo Maximo: Unassigning work order ${workOrderId}`);
      
      let sourceTeamId: string | null = null;
      let workOrderToUnassign: WorkOrder | null = null;
      
      for (const [teamId, workOrders] of Object.entries(teamWorkOrders)) {
        const found = workOrders.find(wo => wo.id === workOrderId);
        if (found) {
          sourceTeamId = teamId;
          workOrderToUnassign = found;
          break;
        }
      }
      
      await maximoApi.unassignWorkOrder({ workOrderId });
      
      if (sourceTeamId && workOrderToUnassign) {
        setTeamWorkOrders(prev => ({
          ...prev,
          [sourceTeamId]: prev[sourceTeamId]?.filter(wo => wo.id !== workOrderId) || []
        }));
        
        const unassignedWorkOrder = { 
          ...workOrderToUnassign, 
          assignedTeam: undefined, 
          status: 'WAPPR' as const 
        };
        
        setUnassignedWorkOrders(prev => {
          const exists = prev.some(wo => wo.id === workOrderId);
          return exists ? prev : [unassignedWorkOrder, ...prev];
        });
        
        if (paginationData) {
          setPaginationData(prev => prev ? {
            ...prev,
            total: prev.total + 1,
            data: prev.data.some(wo => wo.id === workOrderId) 
              ? prev.data 
              : [unassignedWorkOrder, ...prev.data]
          } : prev);
        }
      }
      
      message.success('Demo: Work order unassigned successfully');
    } catch (error) {
      console.error('Demo Maximo: Error unassigning work order:', error);
      message.error('Failed to unassign work order in demo');
      loadInitialData(currentPage, searchText, priorityFilter, workTypeFilter);
      if (teams.length > 0) {
        loadTeamWorkOrders(teams);
      }
    } finally {
      setAssigningWorkOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(workOrderId);
        return newSet;
      });
    }
  };

  // Scroll functions for horizontal team layout
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    loadInitialData(1, value, priorityFilter, workTypeFilter);
  };

  const handlePriorityFilter = (value: number | undefined) => {
    setPriorityFilter(value);
    setCurrentPage(1);
    loadInitialData(1, searchText, value, workTypeFilter);
  };

  const handleWorkTypeFilter = (value: string | undefined) => {
    setWorkTypeFilter(value);
    setCurrentPage(1);
    loadInitialData(1, searchText, priorityFilter, value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadInitialData(page, searchText, priorityFilter, workTypeFilter);
  };

  const handleDateFilter = (dateString: string | null) => {
    const newDateFilter = dateString || undefined;
    setDateFilter(newDateFilter);
    console.log('Demo Maximo: Date filter changed to:', newDateFilter || 'ALL DATES');
    if (teams.length > 0) {
      loadTeamWorkOrders(teams, newDateFilter);
    }
  };

  const clearFilters = () => {
    const todayDate = dayjs().format('YYYY-MM-DD');
    setSearchText('');
    setPriorityFilter(undefined);
    setWorkTypeFilter(undefined);
    setDateFilter(todayDate);
    setCurrentPage(1);
    loadInitialData(1);
    if (teams.length > 0) {
      loadTeamWorkOrders(teams, todayDate);
    }
  };

  const handleWorkOrderCreated = () => {
    setCreateWorkOrderVisible(false);
    loadInitialData(currentPage, searchText, priorityFilter, workTypeFilter);
    message.success('Demo: Work order created successfully');
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <style>
        {`
          .horizontal-scroll-container::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      <Layout className="h-full">
        <Sider width={350} className="bg-white border-r">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <Title level={4} className="mb-0">
                Unassigned Work Orders
              </Title>
              {paginationData && (
                <Badge count={paginationData.total} showZero style={{ backgroundColor: '#ec4899' }} />
              )}
            </div>
            
            {/* Search and Filters */}
            <div className="space-y-3 mb-4">
              <Input.Search
                placeholder="Search work orders..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                allowClear
                prefix={<SearchOutlined />}
              />
              
              <div className="grid grid-cols-2 gap-2">
                <Select
                  placeholder="Priority"
                  value={priorityFilter}
                  onChange={handlePriorityFilter}
                  allowClear
                  size="small"
                >
                  <Select.Option value={1}>P1 - Critical</Select.Option>
                  <Select.Option value={2}>P2 - High</Select.Option>
                  <Select.Option value={3}>P3 - Medium</Select.Option>
                  <Select.Option value={4}>P4 - Low</Select.Option>
                  <Select.Option value={5}>P5 - Lowest</Select.Option>
                </Select>
                
                <Select
                  placeholder="Work Type"
                  value={workTypeFilter}
                  onChange={handleWorkTypeFilter}
                  allowClear
                  size="small"
                >
                  <Select.Option value="CM">CM - Corrective</Select.Option>
                  <Select.Option value="PM">PM - Preventive</Select.Option>
                  <Select.Option value="PROJECT">Project</Select.Option>
                </Select>
              </div>
              
              {(searchText || priorityFilter || workTypeFilter) && (
                <Button size="small" onClick={clearFilters} block>
                  Clear Filters
                </Button>
              )}
            </div>

            <UnassignedColumn
              workOrders={unassignedWorkOrders}
              onDrop={handleUnassignWorkOrder}
              loading={loading}
              assigningWorkOrders={assigningWorkOrders}
              paginationData={paginationData}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </Sider>
        <Content className="p-4 bg-gray-50">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 mb-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Title level={4} className="mb-0 text-lg">
                Team Work Orders - Demo
              </Title>
              <Space align="center" size="small" wrap>
                <CalendarOutlined className="text-gray-500" />
                <Text className="text-sm text-gray-600">Filter:</Text>
                <DatePicker
                  placeholder="Select date"
                  size="small"
                  value={dateFilter ? dayjs(dateFilter) : dayjs()}
                  onChange={(date) => handleDateFilter(date?.format('YYYY-MM-DD') || null)}
                  allowClear
                  style={{ width: 140 }}
                />
                <Button 
                  size="small" 
                  type="link" 
                  onClick={() => handleDateFilter(dayjs().format('YYYY-MM-DD'))}
                  className="px-2 text-xs"
                >
                  Today
                </Button>
              </Space>
            </div>
            <Space align="center" size="small" wrap>
              <Button
                type="primary"
                icon={<CalendarOutlined />}
                onClick={() => setCalendarVisible(true)}
                size="small"
                style={{ backgroundColor: '#ec4899', borderColor: '#ec4899' }}
              >
                Calendar
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateWorkOrderVisible(true)}
                size="small"
                style={{ backgroundColor: '#ec4899', borderColor: '#ec4899' }}
              >
                Create WO
              </Button>
            </Space>
          </div>
          
          {/* Horizontal Scrollable Team Columns */}
          <div className="relative">
            {/* Scroll Buttons */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10">
              <Button 
                type="primary" 
                shape="circle" 
                icon={<LeftOutlined />}
                onClick={scrollLeft}
                size="small"
                className="shadow-lg"
                style={{ backgroundColor: '#ec4899', borderColor: '#ec4899' }}
              />
            </div>
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10">
              <Button 
                type="primary" 
                shape="circle" 
                icon={<RightOutlined />}
                onClick={scrollRight}
                size="small"
                className="shadow-lg"
                style={{ backgroundColor: '#ec4899', borderColor: '#ec4899' }}
              />
            </div>
            
            {/* Horizontal Scrollable Container */}
            <div 
              ref={scrollContainerRef}
              className="horizontal-scroll-container flex gap-4 overflow-x-auto overflow-y-hidden pb-4 px-8"
              style={{ 
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                height: 'calc(100vh - 200px)'
              }}
            >
              {teams.map((team) => (
                <div 
                  key={team.id} 
                  className="flex-none" 
                  style={{ width: '300px', height: '100%' }}
                >
                  <TeamColumn
                    team={team}
                    workOrders={teamWorkOrders[team.id] || []}
                    onDrop={handleWorkOrderDrop}
                    loading={loading}
                    assigningWorkOrders={assigningWorkOrders}
                  />
                </div>
              ))}
            </div>
          </div>
        </Content>
      </Layout>

      {/* Calendar Modal */}
      <Modal
        title="Demo Maximo Calendar"
        open={calendarVisible}
        onCancel={() => setCalendarVisible(false)}
        width="100%"
        style={{ top: 0 }}
        styles={{ body: { height: '100vh', padding: 0 } }}
        footer={null}
      >
        <WorkOrderCalendar
          teams={teams}
          onClose={() => setCalendarVisible(false)}
        />
      </Modal>

      {/* Create Work Order Modal */}
      <CreateWorkOrderForm
        visible={createWorkOrderVisible}
        onClose={() => setCreateWorkOrderVisible(false)}
        onSuccess={handleWorkOrderCreated}
        teams={teams}
      />
    </DndProvider>
  );
};

export default DemoMaximoWorkOrders;